import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Grid, IconButton, Typography } from '@mui/material';
import { PrimaryButon } from '@ui/components/common/button/PrimaryButon';
import { PageHeader } from '../../../components/common/header/PageHeader';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InvoiceDetail } from './InvoiceDetail';
import { useActorBoundary } from '@application/common/hooks/useActorBoundary';
import { Edit, Eye } from 'iconsax-react';
import { CustomChip } from '@ui/components/common/chip/Chip';
import { PrimaryGrid } from '@ui/components/common/grid/PrimaryGrid';
import { WidgetHeader } from '@ui/components/common/header/widgetHeader/WidgetHeader';
import CustomSelect from '@ui/components/common/inputs/CustomSelect';
import { months } from '@ui/constants/helper';
import { AddInvoice } from './modal/AddInvoice';
import { useAppDispatch } from '@application/store/useStore';
import { getAllInvoices } from '@application/features/invoices/invoiceThunk';
import { processingLabel, providerLabel, routeLabel } from '@application/features/invoices/models/InvoiceSummary';
import { downloadCOCDocumentAsync } from '@application/features/freelancer/freelancerThunks';
import { convertBase64toBlob, getMimeTypeFromBase64, openOrDownloadPdf } from '@application/common/helpers/file-helper';
export const Invoices = () => {
    const { t } = useTranslation('faqtoring');
    const actor = useActorBoundary();
    const requestSequence = useRef(0);
    const [detailId, setDetailId] = useState();
    const currentDate = new Date();
    const [isAddInvoice, setIsAddInvoice] = useState(false);
    const [isEditInvoice, setIsEditInvoice] = useState(false);
    const currentMonthIndex = currentDate.getMonth() < 12 ? currentDate.getMonth() + 1 : currentDate.getMonth();
    const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear().toString());
    const [selectedMonth, setSelectedMonth] = useState(currentMonthIndex.toString());
    const [allInvoices, setAllIvoices] = useState([]);
    const [selectedRow, setSelectedRow] = useState('');
    const currentYear = currentDate.getFullYear();
    const monthOptions = months;
    const yearOptions = [
        { label: (currentYear - 1).toString(), value: (currentYear - 1).toString() },
        { label: currentYear.toString(), value: currentYear.toString() }
    ];
    const dispatch = useAppDispatch();
    const fetchInvoices = async (month, year) => {
        if (!actor.check())
            return;
        const sequence = ++requestSequence.current;
        setAllIvoices([]);
        const getInvoiceRequestModel = {
            month,
            year
        };
        await dispatch(getAllInvoices(getInvoiceRequestModel))
            .unwrap()
            .then((resp) => {
            if (sequence !== requestSequence.current || !actor.check())
                return;
            const formattedResp = resp.map((invoice) => ({
                ...invoice,
                totalAmount: parseFloat(String(invoice.totalAmount).replace(/[€$]/g, '')) // Remove $ and parse to number
                    .toLocaleString('nl', { maximumFractionDigits: 2 })
            }));
            setAllIvoices(formattedResp);
        })
            .catch((err) => {
            console.error(err);
        });
    };
    useEffect(() => {
        if (selectedYear && selectedMonth) {
            fetchInvoices(parseInt(selectedMonth), parseInt(selectedYear));
        }
    }, [selectedYear, selectedMonth, currentMonthIndex]);
    const handleYearChange = (event) => {
        setSelectedYear(event.target.value);
    };
    const handleMonthChange = (event) => {
        setSelectedMonth(event.target.value);
    };
    const handleActionClick = (rowData) => {
        if (rowData.summary?.canEdit === true) {
            setSelectedRow(rowData.id);
            setIsEditInvoice(true);
        }
    };
    const DownloadApprovedInvoiceDocument = (rowData) => {
        const fileDto = { folderName: rowData?.folderName, fileName: rowData?.fileName };
        dispatch(downloadCOCDocumentAsync(fileDto))
            .unwrap()
            .then((res) => {
            const mimeType = getMimeTypeFromBase64(res.file);
            if (mimeType === 'application/pdf') {
                openOrDownloadPdf(res.file, rowData?.fileName || 'document', rowData?.fileName || 'Chamber of Commerce Document');
            }
            else {
                const blob = convertBase64toBlob(res.file, mimeType ?? '');
                if (!blob) {
                    console.error('Failed to create Blob from the base64 string.');
                    return;
                }
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = rowData?.fileName || 'download';
                link.click();
                URL.revokeObjectURL(url);
            }
        });
    };
    const updateSelectedYearAndMonth = (year, month) => {
        setSelectedYear(year);
        setSelectedMonth(month.toString());
        if (year === selectedYear && month.toString() === selectedMonth) {
            fetchInvoices(month, parseInt(year));
        }
    };
    const columns = [
        {
            field: 'invoiceNo',
            headerName: 'ID',
            flex: 1.5,
            minWidth: 100
        },
        {
            field: 'route',
            headerName: t('invoiceRouteTitle'),
            flex: 1.3,
            minWidth: 110,
            renderCell: (params) => _jsx(CustomChip, { value: routeLabel(params.row.summary, t) })
        },
        {
            field: 'clientName',
            headerName: 'Client Name',
            flex: 1,
            minWidth: 200
        },
        {
            field: 'totalAmount',
            headerName: 'Amount',
            flex: 1,
            minWidth: 130
        },
        {
            field: 'month',
            headerName: 'Month',
            flex: 1,
            minWidth: 130
        },
        {
            field: 'status',
            headerName: t('invoiceProcessingTitle'),
            flex: 1.3,
            minWidth: 130,
            renderCell: (params) => _jsx(CustomChip, { value: processingLabel(params.row.summary, t) })
        },
        {
            field: 'payout', headerName: t('payoutStatus'), minWidth: 200, flex: 1,
            renderCell: params => _jsxs(Typography, { variant: 'body2', children: [params.row.summary?.providerStatusIsStale && `${t('recoveryLastKnownStatus')}: `, providerLabel(params.row.summary?.payoutStatus, t)] })
        },
        {
            field: 'collection', headerName: t('collection'), minWidth: 200, flex: 1,
            renderCell: params => _jsxs(Typography, { variant: 'body2', children: [params.row.summary?.providerStatusIsStale && `${t('recoveryLastKnownStatus')}: `, providerLabel(params.row.summary?.collectionStatus, t)] })
        },
        {
            field: 'action',
            headerName: 'Action',
            headerAlign: 'left',
            align: 'left',
            flex: 1,
            minWidth: 130,
            renderCell: (params) => _jsxs(_Fragment, { children: [_jsx(IconButton, { "aria-label": t('invoiceDetail'), onClick: () => setDetailId(params.row.id), children: _jsx(Eye, {}) }), params.row.summary?.canEdit === true && _jsx(IconButton, { "aria-label": t('edit'), onClick: () => handleActionClick(params.row), children: _jsx(Edit, {}) })] })
        }
    ];
    return (_jsxs("div", { children: [_jsx(PageHeader, { title: 'Invoices', children: _jsx(Box, { width: 250, children: _jsx(PrimaryButon, { onClick: () => setIsAddInvoice(true), children: "Add Invoice" }) }) }), _jsx(Box, { pl: 2, children: _jsxs(Grid, { container: true, spacing: 3, children: [_jsx(Grid, { item: true, md: 3, xs: 12, sm: 12, children: _jsxs(Box, { display: 'flex', flexDirection: 'column', gap: 2, children: [_jsx(CustomSelect, { options: yearOptions, label: 'Year', selectedOption: selectedYear, handleChange: handleYearChange }), _jsx(CustomSelect, { options: monthOptions, label: 'Month', selectedOption: selectedMonth, handleChange: handleMonthChange })] }) }), _jsxs(Grid, { item: true, md: 9, xs: 12, sm: 12, children: [_jsx(WidgetHeader, { children: _jsx(Typography, { fontSize: '20px', color: '#FFFFFF', children: "All Invoices" }) }), _jsx(PrimaryGrid, { h: 'calc(100svh - 290px)', columns: columns, rows: allInvoices })] })] }) }), detailId && _jsx(InvoiceDetail, { invoiceId: detailId, onClose: () => { setDetailId(undefined); if (selectedMonth && selectedYear)
                    void fetchInvoices(Number(selectedMonth), Number(selectedYear)); } }), isAddInvoice && _jsx(AddInvoice, { open: isAddInvoice, type: 'add', handleClose: () => setIsAddInvoice(false), updateParentYearAndMonth: updateSelectedYearAndMonth }), isEditInvoice && _jsx(AddInvoice, { open: isEditInvoice, type: 'edit', handleClose: () => setIsEditInvoice(false), updateParentYearAndMonth: updateSelectedYearAndMonth, invoiceId: selectedRow })] }));
};
